export type InputMask = 'number' | 'time' | 'date' | 'money' | false;
export type CaretPreservingInputMask = Extract<InputMask, 'number' | 'money'>;

interface FormatInputValueOptions {
  mask: InputMask;
  firstLetterUpperCase: boolean;
  upperCase: boolean;
  lowerCase: boolean;
  maxLength: number | boolean;
}

const firstLetterToUpperCase = (value: string): string => {
  if (!value || value.length === 0) {
    return '';
  }

  return value[0]!.toUpperCase() + value.slice(1);
};

const applyMoneyMask = (value: string): string => {
  let formattedValue = '';
  let hasDecimalSeparator = false;
  let decimalDigitsCount = 0;

  for (const character of value) {
    if (/\d/.test(character)) {
      if (!hasDecimalSeparator || decimalDigitsCount < 2) {
        formattedValue += character;
      }

      if (hasDecimalSeparator) {
        decimalDigitsCount += 1;
      }

      continue;
    }

    if ((character === ',' || character === '.') && formattedValue.length > 0 && !hasDecimalSeparator) {
      formattedValue += ',';
      hasDecimalSeparator = true;
    }
  }

  return formattedValue;
};

const applyDateMask = (value: string): string => {
  // Убираем все символы кроме цифр и точек
  let cleanValue = value.replace(/[^0-9.]/g, '');

  // Ограничиваем длину до 10 символов (dd.mm.yyyy)
  cleanValue = cleanValue.substring(0, 10);

  // Разбираем на части
  const parts = cleanValue.split('.');
  let formattedValue = '';

  if (parts.length >= 1 && parts[0]) {
    // День: ограничиваем до 2 цифр и проверяем диапазон 01-31
    let day = parts[0].replace(/\D/g, '').substring(0, 2);

    if (day.length === 2) {
      const dayNum = parseInt(day, 10);

      if (dayNum > 31) day = '31';

      if (dayNum < 1) day = '01';
    }

    formattedValue = day;

    if (parts.length >= 2 && parts[1]) {
      // Месяц: ограничиваем до 2 цифр и проверяем диапазон 01-12
      let month = parts[1].replace(/\D/g, '').substring(0, 2);

      if (month.length === 2) {
        const monthNum = parseInt(month, 10);

        if (monthNum > 12) month = '12';

        if (monthNum < 1) month = '01';
      }

      formattedValue += '.' + month;

      if (parts.length >= 3 && parts[2]) {
        // Год: ограничиваем до 4 цифр
        const year = parts[2].replace(/\D/g, '').substring(0, 4);
        formattedValue += '.' + year;
      }
    }
  }

  // Автоматически добавляем точки при вводе
  if (formattedValue.length === 2 && !formattedValue.includes('.')) {
    formattedValue += '.';
  } else if (formattedValue.length === 5 && formattedValue.split('.').length === 2) {
    formattedValue += '.';
  }

  return formattedValue;
};

export const applyMask = (mask: InputMask, value: string): string => {
  switch (mask) {
    case 'number':
      return value.replace(/[^0-9]+/g, '');

    case 'time': {
      const digits = value.replace(/\D/g, '');

      return digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
    }

    case 'date':
      return applyDateMask(value);

    case 'money':
      return applyMoneyMask(value);

    default:
      return value;
  }
};

/** Курсор после того же числа сохранённых символов, что и до форматирования. */
export const getFormattedCaretPosition = (
  format: (value: string) => string,
  rawValue: string,
  formattedValue: string,
  rawCaretPosition: number,
): number => {
  const clampedCaretPosition = Math.max(0, Math.min(rawCaretPosition, rawValue.length));
  const preservedCharactersCount = format(rawValue.substring(0, clampedCaretPosition)).length;

  if (preservedCharactersCount === 0) {
    return 0;
  }

  for (let caretPosition = 1; caretPosition <= formattedValue.length; caretPosition += 1) {
    const formattedPrefix = formattedValue.substring(0, caretPosition);

    if (format(formattedPrefix).length >= preservedCharactersCount) {
      return caretPosition;
    }
  }

  return formattedValue.length;
};

export const getMaskedCaretPosition = (
  mask: CaretPreservingInputMask,
  rawValue: string,
  formattedValue: string,
  rawCaretPosition: number,
): number => getFormattedCaretPosition((value) => applyMask(mask, value), rawValue, formattedValue, rawCaretPosition);

export const formatInputValue = (value: string, options: FormatInputValueOptions): string => {
  let formattedValue = applyMask(options.mask, value);

  if (options.firstLetterUpperCase && formattedValue.length > 0) {
    formattedValue = firstLetterToUpperCase(formattedValue);
  } else if (options.upperCase) {
    formattedValue = formattedValue.toUpperCase();
  } else if (options.lowerCase) {
    formattedValue = formattedValue.toLowerCase();
  }

  if (options.maxLength && typeof options.maxLength === 'number') {
    formattedValue = formattedValue.substring(0, options.maxLength);
  }

  return formattedValue;
};
